
import { ApprovalStatusType } from '../../../../domain/model/approval-status-type';
import { TrialStatusEnum } from '../../../../domain/model/model-enums';
import { PigGenderType } from '../../../../domain/model/pig-gender-type';
import { UserInfo } from '../../../../domain/model/user-info';
import { StringUuid } from '../../../../domain/model/uuid/string-uuid';
import { WeighingType } from '../../../../domain/model/weighing-type';
import { StoreBatch } from '../../../../storage/data-store/model/master-data/store-batch';
import { StoreFeed } from '../../../../storage/data-store/model/master-data/store-feed';
import { StoreGroup } from '../../../../storage/data-store/model/master-data/store-group';
import { StorePen } from '../../../../storage/data-store/model/master-data/store-pen';
import { StoreSection } from '../../../../storage/data-store/model/master-data/store-section';
import { StoreTrial } from '../../../../storage/data-store/model/master-data/store-trial';
import { StoreTrialDates } from '../../../../storage/data-store/model/master-data/store-trial-dates';
import { StoreTrialPhase } from '../../../../storage/data-store/model/master-data/store-trial-phase';
import { StoreTrialPhaseType } from '../../../../storage/data-store/model/master-data/store-trial-phase-type';
import { StoreAnimalWeighing } from '../../../../storage/data-store/model/registrations/store-animal-weighing';
import { StoreApprovalStatus } from '../../../../storage/data-store/model/registrations/store-approval-status';
import { StoreBatchWeighing } from '../../../../storage/data-store/model/registrations/store-batch-weighing';
import { StoreUpdateInfo } from '../../../../storage/store-update-info';
import * as afpdata from './afp1951_02_06_2025.json';

type TrialPhaseJson = {
  id: string;
  number: number;
  type: {
    name: string;
  };
  expectedStartDate: null;
  expectedEndDate: null;
  expectedStartWeightKg: null;
  expectedEndWeightKg: null;
}[];

type ApprovalStatusJson = {
  id: string;
  date: string;
  approvedBy: {
    userId: string;
    name: string;
  };
  status: {
    status: string;
  };
}[];

export const AfpTestData = {
  animalWeighings: loadAnimalWeighingsFromJson(),
  batchWeighings: loadBatchWeighingFromJson(),
  pens: loadPensFromJson(),
  groups: loadGroupsFromJson(), // No groups in the test data
  feeds: loadFeedsFromJson(), // No feeds in the test data
  sections: loadSectionsFromJson(),
  trials: loadTrialsFromJson(), // No trials in the test data
  batches: loadBatchesFromJson(),
};

export function loadTrialsFromJson(): Record<string, StoreTrial> {
  const retTrials: Record<string, StoreTrial> = {};
  Object.values(afpdata.trials).forEach((t) => {
    retTrials[t.id] = new StoreTrial(
      new StringUuid(t.id),
      t.name,
      t.trialIdentifier,
      t.description,
      TrialStatusEnum.InProgress,
      new UserInfo(t.createdBy.userId, t.createdBy.name),
      new StoreTrialDates(
        new Date(t.trialDates.expectedStartDate),
        new Date(t.trialDates.expectedEndDate),
        new Date(t.trialDates.startDate),
        new Date(t.trialDates.endDate)
      ),
      mapTrialPhasesFromJson(t.phases),
      t.sectionsIds,
      t.penIds,
      t.batchIds
    );
  });
  return retTrials;
}

export function loadFeedsFromJson(): Record<string, StoreFeed> {
  const retFeeds: Record<string, StoreFeed> = {};
  Object.values(afpdata.feeds).forEach((f) => {
    retFeeds[f.id] = new StoreFeed(new StringUuid(f.id), f.name, f.number);
  });
  return retFeeds;
}

export function loadGroupsFromJson(): Record<string, StoreGroup> {
  const retGroups: Record<string, StoreGroup> = {};
  Object.values(afpdata.groups).forEach((g) => {
    retGroups[g.id] = new StoreGroup(new StringUuid(g.id), g.name, g.number);
  });
  return retGroups;
}

export function loadPensFromJson(): Record<string, StorePen> {
  const retPens: Record<string, StorePen> = {};
  Object.values(afpdata.pens).forEach((p) => {
    retPens[p.id] = new StorePen(new StringUuid(p.id), new StringUuid(p.sectionId), p.name, p.number);
  });
  return retPens;
}

export function loadBatchesFromJson(): Record<string, StoreBatch> {
  const retBatches: Record<string, StoreBatch> = {};
  Object.values(afpdata.batches).forEach((b) => {
    retBatches[b.id] = new StoreBatch(new StringUuid(b.id), b.name, b.number);
  });
  return retBatches;
}

export function loadSectionsFromJson(): Record<string, StoreSection> {
  const retSections: Record<string, StoreSection> = {};
  Object.values(afpdata.sections).forEach((s) => {
    retSections[s.id] = new StoreSection(new StringUuid(s.id), s.name);
  });
  return retSections;
}

export function loadAnimalWeighingsFromJson(): Record<string, StoreAnimalWeighing> {
  const retAnimalWeighings: Record<string, StoreAnimalWeighing> = {};

  Object.values(afpdata.animalWeghings).forEach((aw) => {
    retAnimalWeighings[aw.id] = new StoreAnimalWeighing(
      new StringUuid(aw.id),
      new StringUuid(aw.batchWeighingId),
      new StringUuid(aw.sectionId),
      new StringUuid(aw.trialId),
      new StringUuid(aw.penId),
      new StringUuid(aw.groupId),
      new StringUuid(aw.feedId),
      new WeighingType(aw.type.type),
      new StoreUpdateInfo(
        aw.update.lastServerUpdate ? new Date(aw.update.lastServerUpdate) : null,
        aw.update.lastLocalUpdate ? new Date(aw.update.lastLocalUpdate) : null,
        new UserInfo(aw.update.lastEditedByUser.userId, aw.update.lastEditedByUser.name)
      ),
      aw.inputNumber,
      aw.inputWeight
    );
  });
  return retAnimalWeighings;
}

export function loadBatchWeighingFromJson(): Record<string, StoreBatchWeighing> {
  const retBatchWeighings: Record<string, StoreBatchWeighing> = {};

  Object.values(afpdata.batchWeighings).forEach((bw) => {
    retBatchWeighings[bw.id] = new StoreBatchWeighing(
      new StringUuid(bw.id),
      new StringUuid(bw.batchId),
      new StringUuid(bw.sectionId),
      new StringUuid(bw.trialId),
      bw.sequenceNumber,
      bw.animalWeighingIds,
      mapApprovalsFromJson(bw.approvals),
      new WeighingType(bw.weighingType.type),
      new PigGenderType(bw.gender.id, bw.gender.name),
      new StoreUpdateInfo(
        bw.update.lastLocalUpdate ? new Date(bw.update.lastLocalUpdate) : null,
        bw.update.lastServerUpdate ? new Date(bw.update.lastServerUpdate) : null,
        new UserInfo(bw.update.lastEditedByUser.userId, bw.update.lastEditedByUser.name)
      ),
    );
  });

  return retBatchWeighings;
}

function mapTrialPhasesFromJson(input: TrialPhaseJson): StoreTrialPhase[] {
  return input.map((phase) => {
    return new StoreTrialPhase(
      new StringUuid(phase.id),
      phase.number,
      new StoreTrialPhaseType(phase.type.name),
      phase.expectedStartDate ? new Date(phase.expectedStartDate) : null,
      phase.expectedEndDate ? new Date(phase.expectedEndDate) : null,
      phase.expectedStartWeightKg,
      phase.expectedEndWeightKg
    );
  });
}

function mapApprovalsFromJson(input: ApprovalStatusJson): StoreApprovalStatus[] {
  return input.map((approval) => {
    return new StoreApprovalStatus(
      new StringUuid(approval.id),
      new Date(approval.date),
      new UserInfo(approval.approvedBy.userId, approval.approvedBy.name),
      new ApprovalStatusType(approval.status.status)
    );
  });
}
